import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons'
import { numberStepperStyles } from '../style/number-stepper-styles'
import '@maersk-global/mds-components-core/mc-icon'

@customElement('number-stepper')
export class NumberStepper extends LitElement {
    @property({ type: String }) label = ''
    @property({ type: Number }) value = 0
    @property({ type: Number }) resetValue = 0
    @property({ type: Number }) minValue = 0
    @property({ type: Number }) maxValue = 100
    @property({ type: Number }) incrementValue = 1
    @property({ type: Boolean }) isContinious = false
    @property({ type: String }) textAlign: 'left' | 'right' = 'left'

    @state() private errorMessage = ''

    static styles = numberStepperStyles

    private dispatchValueChange() {
        if (!this.errorMessage) {
            this.dispatchEvent(
                new CustomEvent('value-change', {
                    detail: this.value,
                    bubbles: true,
                    composed: true,
                }),
            )
        }
    }

    private validateInput(value: number) {
        if (isNaN(value) || value < this.minValue || value > this.maxValue) {
            this.errorMessage = 'Please enter a valid number'
        } else {
            this.errorMessage = ''
        }
    }

    private handleInputChange(e: Event) {
        const target = e.target as HTMLInputElement
        const newValue = Number(target.value)

        this.validateInput(newValue)

        if (!this.errorMessage) {
            this.value = newValue
            this.dispatchValueChange()
        }
    }

    private increment() {
        const newValue = this.value + this.incrementValue
        if (newValue <= this.maxValue) {
            this.errorMessage = ''
            this.value = newValue
            this.dispatchValueChange()
        } else if (this.isContinious) {
            this.value = this.minValue + this.incrementValue;
            this.dispatchValueChange()
        }
    }

    private decrement() {
        const newValue = this.value - this.incrementValue
        if (newValue >= this.minValue) {
            this.errorMessage = ''
            this.value = newValue
            this.dispatchValueChange()
        } else if (this.isContinious) {
            this.value = this.maxValue - this.incrementValue;
            this.dispatchValueChange()
        }
    }

    private reset() {
        this.value = this.resetValue
        this.dispatchValueChange()
        this.errorMessage = ''
    }

    private get wrapperClass() {
        return this.textAlign === 'right' ? 'align-right' : 'align-left'
    }
    private get errorClass() {
        return this.textAlign === 'right' ? 'error-right' : 'error-left'
    }

    private get borderClass() {
        return this.errorMessage !== '' ? 'border--error' : ''
    }

    render() {
        const isInvalid = this.errorMessage !== ''

        return html`
            <div class="relative">
                <label for="number-input" class="input-label"
                    >${this.label}</label
                >
                <div class="number-stepper-wrapper ${this.wrapperClass}">
                    <div class="number-stepper border ${this.borderClass}">
                        <input
                            id="number-input"
                            type="number"
                            .value=${String(this.value)}
                            @input=${this.handleInputChange}
                            class="number-input ${isInvalid
                                ? 'invalid-input'
                                : ''}"
                        />
                        <div class="buttons-wrapper">
                            <button
                                class="button button-increment"
                                @click=${this.decrement}
                                ?disabled=${this.value <= this.minValue && !this.isContinious}
                            >
                                <mc-icon icon="chevron-left"></mc-icon>
                            </button>
                            <button
                                class="button button-increment"
                                @click=${this.increment}
                                ?disabled=${this.value >= this.maxValue && !this.isContinious}
                            >
                                <mc-icon icon="chevron-right"></mc-icon>
                            </button>
                        </div>
                    </div>
                    <mc-tooltip>
                    <button
                        class="button reset-button border"
                        @click=${this.reset}
                        slot="trigger"
                    >
                        <mc-icon icon="arrow-anti-clockwise"></mc-icon>
                    </button>
                    ${this.label === "Adjust heading (degree)"?html`<span>Reset Heading</span>`:html`<span>Reset Speed</span>`}
                    </mc-tooltip>
                </div>
                ${isInvalid
                    ? html`<div class="error ${this.errorClass}">
                          Please enter a valid number
                      </div>`
                    : ''}
            </div>
        `
    }
}
